import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@project/lib/data-client";
import { useI18n } from "@project/lib/use-i18n";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Bed,
  Stethoscope,
  Utensils,
  Heart,
  Users,
  Sparkles,
} from "lucide-react";
import { HERO_IMAGES } from "@project/lib/stock-images";

const items = [
  {
    icon: Bed,
    titleKey: "care.residentCare.items.comfortableStay.title",
    descKey: "care.residentCare.items.comfortableStay.desc",
  },
  {
    icon: Utensils,
    titleKey: "care.residentCare.items.nutritiousMeals.title",
    descKey: "care.residentCare.items.nutritiousMeals.desc",
  },
  {
    icon: Stethoscope,
    titleKey: "care.residentCare.items.medicalCare.title",
    descKey: "care.residentCare.items.medicalCare.desc",
  },
  {
    icon: Users,
    titleKey: "care.residentCare.items.companionship.title",
    descKey: "care.residentCare.items.companionship.desc",
  },
  {
    icon: Heart,
    titleKey: "care.residentCare.items.emotionalSupport.title",
    descKey: "care.residentCare.items.emotionalSupport.desc",
  },
  {
    icon: Sparkles,
    titleKey: "care.residentCare.items.ritualsDignity.title",
    descKey: "care.residentCare.items.ritualsDignity.desc",
  },
];

const empty = {
  // Resident
  resident_name: "",
  gender: "",
  date_of_birth: "",
  blood_group: "",
  marital_status: "",
  religion: "Jain",
  jain_status: "",
  caste: "",
  education: "",
  occupation: "",
  resident_mobile: "",
  aadhaar: "",
  identification_mark: "",
  previous_address: "",
  city: "",
  state: "",
  pincode: "",
  // Health
  health_conditions: "",
  current_medications: "",
  mobility: "Independent",
  // Admission
  admission_type: "Free",
  monthly_maintenance: "",
  preferred_admission_date: "",
  reason_for_admission: "",
  referred_by: "",
  // Family / Applicant
  applicant_name: "",
  relation: "",
  applicant_mobile: "",
  alt_mobile: "",
  applicant_email: "",
  applicant_address: "",
  emergency_contact_name: "",
  emergency_contact_mobile: "",
  emergency_relation: "",
  notes: "",
};

export default function ResidentCarePage() {
  const { t } = useI18n();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof empty, v: string) => setF({ ...f, [k]: v });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const lines = Object.entries(f)
      .filter(([_, v]) => String(v).trim() !== "")
      .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
    const { error } = await supabase.from("contact_inquiries").insert({
      full_name: f.applicant_name || f.resident_name,
      mobile: f.applicant_mobile || f.resident_mobile,
      email: f.applicant_email,
      subject: `Resident Admission Enquiry — ${f.resident_name || "(name pending)"}`,
      message: lines.join("\n"),
    });
    setBusy(false);
    if (error) {
      return toast.error(t("care.residentCare.toast.error"));
    }
    toast.success(t("care.residentCare.toast.success"));
    setF(empty);
  };

  return (
    <PageShell>
      <PageHero
        eyebrow={t("care.residentCare.hero.eyebrow")}
        title={t("care.residentCare.hero.title")}
        subtitle={t("care.residentCare.hero.subtitle")}
        image={HERO_IMAGES.residentCare}
      />
      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-12 md:grid-cols-2 lg:grid-cols-3">
        {items.map(i => (
          <div
            key={i.titleKey}
            className="rounded-2xl border border-border bg-card p-6"
          >
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary text-gold">
              <i.icon className="h-6 w-6" />
            </div>
            <h3 className="mt-4 font-serif text-xl font-bold text-primary">
              {t(i.titleKey)}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">{t(i.descKey)}</p>
          </div>
        ))}
      </div>

      <section className="mx-auto max-w-4xl px-4 pb-20">
        <div className="mb-6 text-center">
          <p className="text-sm uppercase tracking-widest text-gold">
            {t("care.residentCare.form.eyebrow")}
          </p>
          <h2 className="mt-2 font-serif text-2xl font-bold text-primary md:text-3xl">
            {t("care.residentCare.form.heading")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("care.residentCare.form.description")}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-sm"
        >
          <Section title={t("care.residentCare.form.sections.resident")}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>{t("care.residentCare.form.labels.residentName")}</Label>
                <Input
                  required
                  value={f.resident_name}
                  onChange={e => set("resident_name", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.gender")}</Label>
                <Select value={f.gender} onValueChange={v => set("gender", v)}>
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("care.residentCare.form.select")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">
                      {t("care.residentCare.form.options.gender.male")}
                    </SelectItem>
                    <SelectItem value="Female">
                      {t("care.residentCare.form.options.gender.female")}
                    </SelectItem>
                    <SelectItem value="Other">
                      {t("care.residentCare.form.options.gender.other")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.dob")}</Label>
                <Input
                  type="date"
                  value={f.date_of_birth}
                  onChange={e => set("date_of_birth", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.bloodGroup")}</Label>
                <Input
                  value={f.blood_group}
                  onChange={e => set("blood_group", e.target.value)}
                  placeholder={t(
                    "care.residentCare.form.placeholders.bloodGroup"
                  )}
                />
              </div>
              <div>
                <Label>
                  {t("care.residentCare.form.labels.maritalStatus")}
                </Label>
                <Select
                  value={f.marital_status}
                  onValueChange={v => set("marital_status", v)}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("care.residentCare.form.select")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single">
                      {t("care.residentCare.form.options.marital.single")}
                    </SelectItem>
                    <SelectItem value="Married">
                      {t("care.residentCare.form.options.marital.married")}
                    </SelectItem>
                    <SelectItem value="Widowed">
                      {t("care.residentCare.form.options.marital.widowed")}
                    </SelectItem>
                    <SelectItem value="Divorced">
                      {t("care.residentCare.form.options.marital.divorced")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.religion")}</Label>
                <Input
                  value={f.religion}
                  onChange={e => set("religion", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.jainStatus")}</Label>
                <Select
                  value={f.jain_status}
                  onValueChange={v => set("jain_status", v)}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("care.residentCare.form.select")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Shwetambar">
                      {t("care.residentCare.form.options.jain.shwetambar")}
                    </SelectItem>
                    <SelectItem value="Digambar">
                      {t("care.residentCare.form.options.jain.digambar")}
                    </SelectItem>
                    <SelectItem value="Sthanakvasi">
                      {t("care.residentCare.form.options.jain.sthanakvasi")}
                    </SelectItem>
                    <SelectItem value="Terapanthi">
                      {t("care.residentCare.form.options.jain.terapanthi")}
                    </SelectItem>
                    <SelectItem value="Other">
                      {t("care.residentCare.form.options.jain.other")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.caste")}</Label>
                <Input
                  value={f.caste}
                  onChange={e => set("caste", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.education")}</Label>
                <Input
                  value={f.education}
                  onChange={e => set("education", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.occupation")}</Label>
                <Input
                  value={f.occupation}
                  onChange={e => set("occupation", e.target.value)}
                />
              </div>
              <div>
                <Label>
                  {t("care.residentCare.form.labels.residentMobile")}
                </Label>
                <Input
                  value={f.resident_mobile}
                  onChange={e => set("resident_mobile", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.aadhaar")}</Label>
                <Input
                  value={f.aadhaar}
                  onChange={e => set("aadhaar", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>
                  {t("care.residentCare.form.labels.identificationMark")}
                </Label>
                <Input
                  value={f.identification_mark}
                  onChange={e => set("identification_mark", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>
                  {t("care.residentCare.form.labels.previousAddress")}
                </Label>
                <Textarea
                  rows={2}
                  value={f.previous_address}
                  onChange={e => set("previous_address", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.city")}</Label>
                <Input
                  value={f.city}
                  onChange={e => set("city", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.state")}</Label>
                <Input
                  value={f.state}
                  onChange={e => set("state", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.pincode")}</Label>
                <Input
                  value={f.pincode}
                  onChange={e => set("pincode", e.target.value)}
                />
              </div>
            </div>
          </Section>

          <Section title={t("care.residentCare.form.sections.health")}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>
                  {t("care.residentCare.form.labels.healthConditions")}
                </Label>
                <Textarea
                  rows={2}
                  value={f.health_conditions}
                  onChange={e => set("health_conditions", e.target.value)}
                  placeholder={t(
                    "care.residentCare.form.placeholders.healthConditions"
                  )}
                />
              </div>
              <div className="md:col-span-2">
                <Label>
                  {t("care.residentCare.form.labels.currentMedications")}
                </Label>
                <Textarea
                  rows={2}
                  value={f.current_medications}
                  onChange={e => set("current_medications", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.mobility")}</Label>
                <Select
                  value={f.mobility}
                  onValueChange={v => set("mobility", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Independent">
                      {t("care.residentCare.form.options.mobility.independent")}
                    </SelectItem>
                    <SelectItem value="Walking stick">
                      {t(
                        "care.residentCare.form.options.mobility.walkingStick"
                      )}
                    </SelectItem>
                    <SelectItem value="Walker">
                      {t("care.residentCare.form.options.mobility.walker")}
                    </SelectItem>
                    <SelectItem value="Wheelchair">
                      {t("care.residentCare.form.options.mobility.wheelchair")}
                    </SelectItem>
                    <SelectItem value="Bed-ridden">
                      {t("care.residentCare.form.options.mobility.bedRidden")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          <Section title={t("care.residentCare.form.sections.admission")}>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>
                  {t("care.residentCare.form.labels.admissionType")}
                </Label>
                <Select
                  value={f.admission_type}
                  onValueChange={v => set("admission_type", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Free">
                      {t("care.residentCare.form.options.admission.free")}
                    </SelectItem>
                    <SelectItem value="Paid">
                      {t("care.residentCare.form.options.admission.paid")}
                    </SelectItem>
                    <SelectItem value="Subsidised">
                      {t("care.residentCare.form.options.admission.subsidised")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>
                  {t("care.residentCare.form.labels.monthlyMaintenance")}
                </Label>
                <Input
                  type="number"
                  value={f.monthly_maintenance}
                  onChange={e => set("monthly_maintenance", e.target.value)}
                />
              </div>
              <div>
                <Label>
                  {t("care.residentCare.form.labels.preferredDate")}
                </Label>
                <Input
                  type="date"
                  value={f.preferred_admission_date}
                  onChange={e =>
                    set("preferred_admission_date", e.target.value)
                  }
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.referredBy")}</Label>
                <Input
                  value={f.referred_by}
                  onChange={e => set("referred_by", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>{t("care.residentCare.form.labels.reason")}</Label>
                <Textarea
                  required
                  rows={3}
                  value={f.reason_for_admission}
                  onChange={e => set("reason_for_admission", e.target.value)}
                />
              </div>
            </div>
          </Section>

          <Section title={t("care.residentCare.form.sections.applicant")}>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label>
                  {t("care.residentCare.form.labels.applicantName")}
                </Label>
                <Input
                  required
                  value={f.applicant_name}
                  onChange={e => set("applicant_name", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.relation")}</Label>
                <Input
                  value={f.relation}
                  onChange={e => set("relation", e.target.value)}
                  placeholder={t(
                    "care.residentCare.form.placeholders.relation"
                  )}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.mobile")}</Label>
                <Input
                  required
                  value={f.applicant_mobile}
                  onChange={e => set("applicant_mobile", e.target.value)}
                />
              </div>
              <div>
                <Label>{t("care.residentCare.form.labels.altMobile")}</Label>
                <Input
                  value={f.alt_mobile}
                  onChange={e => set("alt_mobile", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>{t("care.residentCare.form.labels.email")}</Label>
                <Input
                  type="email"
                  value={f.applicant_email}
                  onChange={e => set("applicant_email", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>
                  {t("care.residentCare.form.labels.applicantAddress")}
                </Label>
                <Textarea
                  rows={2}
                  value={f.applicant_address}
                  onChange={e => set("applicant_address", e.target.value)}
                />
              </div>
              <div>
                <Label>
                  {t("care.residentCare.form.labels.emergencyName")}
                </Label>
                <Input
                  value={f.emergency_contact_name}
                  onChange={e => set("emergency_contact_name", e.target.value)}
                />
              </div>
              <div>
                <Label>
                  {t("care.residentCare.form.labels.emergencyMobile")}
                </Label>
                <Input
                  value={f.emergency_contact_mobile}
                  onChange={e =>
                    set("emergency_contact_mobile", e.target.value)
                  }
                />
              </div>
              <div>
                <Label>
                  {t("care.residentCare.form.labels.emergencyRelation")}
                </Label>
                <Input
                  value={f.emergency_relation}
                  onChange={e => set("emergency_relation", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>{t("care.residentCare.form.labels.notes")}</Label>
                <Textarea
                  rows={3}
                  value={f.notes}
                  onChange={e => set("notes", e.target.value)}
                />
              </div>
            </div>
          </Section>

          <Button
            type="submit"
            disabled={busy}
            className="hover:bg-gold/90 w-full bg-gold text-gold-foreground"
          >
            {busy
              ? t("care.residentCare.form.submitting")
              : t("care.residentCare.form.submit")}
          </Button>
        </form>
      </section>
    </PageShell>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4 className="mb-3 font-serif text-lg font-bold text-primary">
        {title}
      </h4>
      {children}
    </div>
  );
}
